import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const S = { padding: '7px 10px', border: '1.5px solid #d1d5db', borderRadius: '6px', fontSize: '12px', background: 'white' };

const AdminPropertiesTab = ({ properties, photoCsvData, photoUpdateResult, photoUpdating, handlePhotoCsvUpload, submitBulkPhotoUpdate, downloadCSV, deleteProperty }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ text: '', state: '', type: '', beds: '', bedsMode: 'exact', rentUnder: '', rentOver: '', status: '' });
  const [sortField, setSortField] = useState('listing_date');
  const [sortDir, setSortDir] = useState('desc');
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearAll = () => { setFilters({ text: '', state: '', type: '', beds: '', bedsMode: 'exact', rentUnder: '', rentOver: '', status: '' }); };

  const filtered = useMemo(() => {
    let list = [...properties];
    if (filters.text) {
      const s = filters.text.toLowerCase();
      list = list.filter(p => (p.city||'').toLowerCase().includes(s) || (p.address||'').toLowerCase().includes(s) || (p.property_id||'').toLowerCase().includes(s) || (p.agency_name||'').toLowerCase().includes(s) || (p.postcode||'').includes(s));
    }
    if (filters.state) list = list.filter(p => p.state === filters.state);
    if (filters.type) list = list.filter(p => p.property_type === filters.type);
    if (filters.beds) {
      const b = parseInt(filters.beds);
      list = filters.bedsMode === '+' ? list.filter(p => (p.total_bedrooms||0) >= b) : list.filter(p => p.total_bedrooms === b);
    }
    if (filters.rentUnder) list = list.filter(p => (p.weekly_rent_per_person||0) < parseFloat(filters.rentUnder));
    if (filters.rentOver)  list = list.filter(p => (p.weekly_rent_per_person||0) > parseFloat(filters.rentOver));
    if (filters.status) list = list.filter(p => p.status === filters.status);

    list.sort((a, b) => {
      let av = a[sortField] ?? '', bv = b[sortField] ?? '';
      if (sortField === 'weekly_rent_per_person' || sortField === 'total_bedrooms') { av = Number(av); bv = Number(bv); }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [properties, filters, sortField, sortDir]);

  const states = ['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'];
  const types = [...new Set(properties.map(p => p.property_type).filter(Boolean))].sort();
  const statuses = [...new Set(properties.map(p => p.status).filter(Boolean))].sort();
  const hasFilters = Object.entries(filters).some(([k,v]) => k !== 'bedsMode' && v !== '');

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const arrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="admin-table-section">
      <div className="section-header">
        <h2>All Properties ({filtered.length}{hasFilters ? ` of ${properties.length}` : ''})</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '8px 14px' }} onClick={() => downloadCSV('/admin/export/properties', 'properties_export.csv')} data-testid="export-properties-btn">Export CSV</button>
          <button className="btn btn-primary" onClick={() => navigate('/properties/add')}>+ Add Property</button>
        </div>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '14px', background: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Suburb / ID / Agency</div>
          <input type="text" value={filters.text} onChange={e => setF('text', e.target.value)} placeholder="free text..." style={{ ...S, width: '180px' }} data-testid="admin-prop-text" />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>State</div>
          <select value={filters.state} onChange={e => setF('state', e.target.value)} style={S} data-testid="admin-prop-state">
            <option value="">All States</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Type</div>
          <select value={filters.type} onChange={e => setF('type', e.target.value)} style={S} data-testid="admin-prop-type">
            <option value="">All Types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Beds</div>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input type="number" value={filters.beds} onChange={e => setF('beds', e.target.value)} placeholder="e.g. 4" style={{ ...S, width: '60px' }} min="1" max="20" data-testid="admin-prop-beds" />
            <select value={filters.bedsMode} onChange={e => setF('bedsMode', e.target.value)} style={{ ...S, padding: '7px 6px' }} data-testid="admin-prop-beds-mode">
              <option value="exact">exact</option>
              <option value="+">+</option>
            </select>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Rent Under $/wk</div>
          <input type="number" value={filters.rentUnder} onChange={e => setF('rentUnder', e.target.value)} placeholder="e.g. 300" style={{ ...S, width: '90px' }} min="0" data-testid="admin-prop-rent-under" />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Rent Over $/wk</div>
          <input type="number" value={filters.rentOver} onChange={e => setF('rentOver', e.target.value)} placeholder="e.g. 100" style={{ ...S, width: '90px' }} min="0" data-testid="admin-prop-rent-over" />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Status</div>
          <select value={filters.status} onChange={e => setF('status', e.target.value)} style={S} data-testid="admin-prop-status">
            <option value="">All</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>Sort</div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <select value={sortField} onChange={e => setSortField(e.target.value)} style={S} data-testid="admin-prop-sort">
              <option value="listing_date">Date</option>
              <option value="city">Suburb</option>
              <option value="state">State</option>
              <option value="weekly_rent_per_person">Rent</option>
              <option value="total_bedrooms">Beds</option>
            </select>
            <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} style={{ ...S, cursor: 'pointer' }} data-testid="admin-prop-sort-dir">{sortDir === 'asc' ? '▲' : '▼'}</button>
          </div>
        </div>
        {hasFilters && <button onClick={clearAll} style={{ ...S, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', cursor: 'pointer', alignSelf: 'flex-end' }} data-testid="admin-prop-clear">Clear All</button>}
        <button style={{ ...S, background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '700', padding: '7px 18px', alignSelf: 'flex-end' }} data-testid="admin-prop-search" onClick={() => document.getElementById('admin-props-table')?.scrollIntoView({ behavior: 'smooth' })}>Search</button>
      </div>

      {/* Bulk Photo Update Tool */}
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }} data-testid="bulk-photo-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <strong style={{ color: '#0369a1', fontSize: '14px' }}>Bulk Photo Update</strong>
          <span style={{ fontSize: '12px', color: '#64748b' }}>Upload a CSV with columns: <code>property_id, image_url</code></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <input type="file" accept=".csv" onChange={handlePhotoCsvUpload} style={{ fontSize: '13px' }} data-testid="photo-csv-upload" />
          {photoCsvData.length > 0 && (
            <button className="btn btn-primary" style={{ fontSize: '13px', padding: '8px 16px' }}
              onClick={submitBulkPhotoUpdate} disabled={photoUpdating} data-testid="bulk-photo-submit">
              {photoUpdating ? 'Updating...' : `Update ${photoCsvData.length} Properties`}
            </button>
          )}
        </div>
        {photoCsvData.length > 0 && !photoUpdateResult && (
          <p style={{ fontSize: '12px', color: '#0369a1', margin: '8px 0 0' }}>{photoCsvData.length} rows loaded. Click "Update" to apply.</p>
        )}
        {photoUpdateResult && (
          <div style={{ marginTop: '10px', padding: '10px 14px', background: photoUpdateResult.updated > 0 ? '#d1fae5' : '#fef2f2', borderRadius: '6px', fontSize: '13px' }} data-testid="photo-update-result">
            <strong>{photoUpdateResult.updated} of {photoUpdateResult.total} properties updated successfully.</strong>
            {photoUpdateResult.not_found > 0 && <span style={{ color: '#b45309', marginLeft: '8px' }}>{photoUpdateResult.not_found} not found.</span>}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><p>No properties match the current filters.</p></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table id="admin-props-table" className="admin-table" style={{ fontSize: '12px', minWidth: '1100px' }}>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('property_id')}>Property ID{arrow('property_id')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('city')}>Location{arrow('city')}</th>
                <th>Type</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('weekly_rent_per_person')}>Singles/wk{arrow('weekly_rent_per_person')}</th>
                <th>Couples/wk</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('total_bedrooms')}>Total Beds{arrow('total_bedrooms')}</th>
                <th>Baths</th>
                <th>Cars</th>
                <th>Groups</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('listing_date')}>Date Listed{arrow('listing_date')}</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((prop) => {
                const r = prop.weekly_rent_per_person || 0;
                const sCRA = Math.min(73.23, Math.max(0, 0.75 * (r - 74.10)));
                const cCRA = Math.min(103.40, Math.max(0, 0.75 * (r - 123.10)));
                const sNet = Math.round(r - sCRA);
                const cNet = Math.round(r - cCRA);
                return (
                  <tr key={prop.property_id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>{prop.property_id?.slice(-10) || '—'}</td>
                    <td>{prop.city} {prop.state}{prop.postcode ? ` ${prop.postcode}` : ''}</td>
                    <td>{prop.property_type}</td>
                    <td style={{ color: '#b91c1c', fontWeight: '700' }}>${sNet}</td>
                    <td style={{ color: '#b91c1c', fontWeight: '700' }}>${cNet}</td>
                    <td style={{ textAlign: 'center' }}>{prop.total_bedrooms || '?'}</td>
                    <td style={{ textAlign: 'center' }}>{prop.total_bathrooms > 0 ? prop.total_bathrooms : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{prop.parking_spaces || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{prop.group_count > 0 ? <span style={{ fontWeight: '700', color: '#2563eb' }}>{prop.group_count}</span> : '—'}</td>
                    <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{prop.listing_date ? new Date(prop.listing_date).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'2-digit' }) : '—'}</td>
                    <td><span className={`status-badge status-${prop.status}`}>{prop.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                        <button className="btn btn-sm" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => window.open(`/properties/${prop.property_id}`, '_blank')} data-testid={`goto-property-${prop.property_id}`}>Go To</button>
                        <button className="btn btn-sm" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => navigate(`/properties/${prop.property_id}/edit`)}>Edit</button>
                        <a className="btn btn-sm" style={{ fontSize: '11px', padding: '3px 8px', textDecoration:'none', background:'#e0f2fe', color:'#0369a1', border:'1px solid #bae6fd' }}
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${prop.address || ''} ${prop.city || ''} ${prop.state || ''} ${prop.postcode || ''} Australia`.trim())}`}
                          target="_blank" rel="noopener noreferrer">Map ↗</a>
                        <button className="btn btn-sm btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => deleteProperty(prop.property_id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPropertiesTab;
